// =============================================================================
// ui/tabs/ImportTrades.tsx — the import control on the Trades tab.
// -----------------------------------------------------------------------------
// The first write surface in this client.
//
// ── 🚨 NOTHING IS WRITTEN WITHOUT A PREVIEW ──
//
// Parse → assemble → dedupe → SHOW → confirm → insert. The old client imports on
// drop and reports afterwards with a toast, which is fine when the dedupe is
// trusted and the book is yours. It is the wrong default for the first write path
// in a new client against a REAL book that already has a duplicate-trades
// incident open: the user should see "37 new, 12 already yours, 2 rejected" and
// choose, rather than read it after the fact.
//
// ⚠ The preview is also where the REJECTED rows are visible. An import that
// silently drops 40 of 200 and says "imported 160" is how someone comes to trust
// a book missing a fifth of their trading.
//
// ── WHAT IS NOT HERE ──
//
// ✅ NOTHING. Every import format the shipping client reads is dispatched here,
// as of 2026-08-02: Sierra, ATAS (CSV, and both XLSX sheet kinds), Tradovate
// (paired and fills), Rithmic, TopstepX, Tradesea, MotiveWave, Trading
// Technologies and Jigsaw.
//
// ⚠ THE CAPTION IS STILL CHECKED BY A TEST, and the check has changed shape
// rather than gone away: it used to assert that the absence list named the
// missing formats, and now asserts there is no absence list at all while every
// dispatched format appears. That test has failed by name FIVE times, each time
// in the same commit as the feature that made it wrong. Keep it that way — the
// moment a format is added or removed, the copy has to move with it.
// =============================================================================

import { useCallback, useRef, useState } from 'react';
import { useAccounts } from '../state/accounts';
import { useContracts } from '../state/contracts';
import { usePreferences } from '../state/preferences';
import { useSupabase } from '../state/supabase';
import {
  parseDelimitedImport, parseXlsxImport, type DegradedImport,
} from '../../io/sync/importFile';
import { readZip, xlsxParts } from '../../io/parsers/zip';
import { assembleImported } from '../../io/sync/assembleImported';
import { insertImportedTrades, newImportBatch } from '../../io/sync/importTrades';
import { loadOpenPositions, recordOpenPositions } from '../../io/sync/openPositions';
import type { CarriedInClosed, OpenPosition } from '../../io/parsers/sierra';
import { createHydrationLedger } from '../../io/sync/hydration';
import { loadTradesInRange } from '../../io/sync/trades';
import { dedupeIncoming, importSummary } from '../../selectors/importDedupe';
import type { Account } from '../../selectors/accounts';
// ⚠ The type comes from the io RESULT, not from domain/. The layering guard
// caught the first version naming `domain/types`.Trade directly, and it was right
// to: what this component actually holds is "whatever assembleImported produced",
// which is a narrower and more honest dependency than the full writable Trade.
import type { AssembleResult } from '../../io/sync/assembleImported';
import { reportFailure } from '../../io/diagnostics/errorLog';

interface Preview {
  fileName: string;
  format: string;
  /** Assembled, deduped, ready to insert. */
  toInsert: AssembleResult['trades'];
  duplicates: number;
  parserSkipped: number;
  carriedIn: number;
  positionMismatches: number;
  /** Fills the exporter backfilled from the broker — see the preview note. */
  unverifiedFills: number;
  totalFills: number;
  openCloseConflicts: number;
  /** Prior positions this file closes — see the preview note and the commit. */
  carriedInClosed: CarriedInClosed[];
  /** What this file leaves open, recorded on commit for the next import. */
  leftOpen: OpenPosition[];
  degraded: DegradedImport | null;
  rejected: { contract: string; reason: string }[];
  unassigned: number;
}

type Phase = 'idle' | 'reading' | 'previewing' | 'writing' | 'done' | 'failed';

export function ImportTrades({ onImported }: { onImported?: () => void }) {
  const { sb, userId } = useSupabase();
  const { accounts } = useAccounts();
  const { contracts } = useContracts();
  const { prefs } = usePreferences();

  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  // ⚠ `null` = auto-detect from the file's own account column, which is the
  // default and the right one. An explicit choice overrides every row.
  const [routeTo, setRouteTo] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const reset = useCallback(() => {
    setPreview(null);
    setMessage(null);
    setPhase('idle');
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const onPick = useCallback(async (file: File) => {
    if (!contracts || !accounts || !prefs) return;
    setPhase('reading');
    setMessage(null);

    try {
      const today = new Date().toISOString().slice(0, 10);

      // ⚠ Routed on the EXTENSION, because an `.xlsx` is a ZIP and its first
      // bytes tell you nothing about which spreadsheet it holds. `readZip`
      // throws a named error on anything that is not an archive, so a
      // mis-extensioned CSV fails with "not a readable ZIP" rather than
      // silently.
      // ⚠ WHAT AN EARLIER IMPORT LEFT YOU HOLDING (#12), read before parsing so
      // a fill that closes one becomes a real cross-session trade instead of a
      // phantom on the opposite side. Returns [] on any failure, and with [] the
      // parsers behave exactly as they did before — so an unreachable store
      // degrades to today rather than blocking the import.
      const prior = await loadOpenPositions(sb, userId);

      const parsed = /\.xlsx$/i.test(file.name)
        ? await (async () => {
          const entries = await readZip(new Uint8Array(await file.arrayBuffer()));
          const parts = xlsxParts(entries);
          return parseXlsxImport(parts.sheets, parts.sharedStrings, contracts, today);
        })()
        : parseDelimitedImport(await file.text(), contracts, today, prior);

      if (parsed.unrecognised) {
        setPhase('failed');
        setMessage(parsed.unrecognised);
        return;
      }

      const assembled = assembleImported(parsed.trades, {
        accounts: accounts as readonly Account[],
        contracts,
        sessionTz: prefs.sessionTimezone,
        accountId: routeTo,
      });

      // ⚠ Deduped against the WHOLE BOOK, not the active range. A trade from March
      // is still a duplicate in July, and scoping this to the visible range would
      // re-import everything outside it.
      const existing = await loadTradesInRange(
        sb, { startDate: null, endDate: null }, prefs.dayBoundaryMode, prefs.sessionTimezone,
      );
      const deduped = dedupeIncoming(assembled.trades, existing.rows);

      setPreview({
        fileName: file.name,
        format: parsed.format ?? 'unknown',
        toInsert: deduped.added,
        duplicates: deduped.skipped,
        parserSkipped: parsed.skipped,
        carriedIn: parsed.carriedIn ?? 0,
        positionMismatches: parsed.positionMismatches ?? 0,
        unverifiedFills: parsed.unverifiedFills ?? 0,
        totalFills: parsed.totalFills ?? 0,
        openCloseConflicts: parsed.openCloseConflicts ?? 0,
        carriedInClosed: parsed.carriedInClosed ?? [],
        // What this file leaves open, so the NEXT import can close it.
        leftOpen: parsed.openPositions ?? [],
        degraded: parsed.degraded ?? null,
        rejected: assembled.rejected.map((r) => ({
          contract: r.row.contract, reason: r.reason,
        })),
        unassigned: assembled.unassigned,
      });
      setPhase('previewing');
    } catch (e) {
      setPhase('failed');
      setMessage(reportFailure('import', e));
    }
  }, [contracts, accounts, prefs, routeTo, sb]);

  const commit = useCallback(async () => {
    if (!preview) return;
    setPhase('writing');

    // ⚠ A FRESH ledger per import. These trades are new here; nothing else in the
    // app should inherit permission to save them.
    const ledger = createHydrationLedger();
    // ⚠ One batch per CONFIRMED import, minted here rather than at parse time:
    // a preview the user cancels must not leave a batch id behind, and a file
    // previewed twice and imported once is one batch, not two.
    const batch = newImportBatch(preview.fileName, preview.format, new Date().toISOString());
    const r = await insertImportedTrades(sb, preview.toInsert, userId, ledger, batch);

    if (r.error) {
      setPhase('failed');
      setMessage(r.error);
      return;
    }

    // ⚠ AFTER the trades are safely written, and only on a CONFIRMED import.
    // Doing it at preview time would mean cancelling a preview quietly consumed
    // positions. A failure here is reported but does not fail the import: the
    // trades are correct either way, and the cost is that the next import will
    // not know about a position — which is exactly today's behaviour.
    const posr = await recordOpenPositions(
      sb, userId, preview.leftOpen,
      preview.carriedInClosed.map((c) => c.fingerprint),
      batch.batchId,
    );
    if (posr.error) setMessage(`Trades imported. Open positions were not recorded: ${posr.error}`);

    setPhase('done');
    setMessage(
      importSummary({ added: preview.toInsert, skipped: preview.duplicates }, preview.fileName),
    );
    setPreview(null);
    onImported?.();
  }, [preview, sb, userId, onImported]);

  const ready = !!contracts && !!accounts && !!prefs;

  /**
   * Open the file browser from the drop zone.
   *
   * 🔴 IT CLICKS THE EXISTING `<input type="file">` AND DOES NOTHING ELSE. The
   * temptation is a handler that reads the file itself, which would pass any
   * "clicking imports a file" test while being a SECOND parse path — the exact
   * thing this file's header warns about, and a second place to forget both the
   * xlsx branch and the preview gate.
   *
   * ⚠ Guarded by the same condition the drop handler refuses on. A picker that
   * opened mid-write would let a file be chosen against an account list that is
   * about to change under it.
   */
  const browse = () => {
    if (!ready || phase === 'writing') return;
    fileRef.current?.click();
  };

  return (
    <div className="card import-card" data-testid="import-trades">
      <h3>Import trades</h3>

      <div className="import-row">
        <label className="range-picker-label" htmlFor="import-into">Import into</label>
        <select
          id="import-into"
          value={routeTo ?? ''}
          onChange={(e) => setRouteTo(e.target.value || null)}
          disabled={phase === 'writing'}
        >
          {/* ⚠ Auto-detect FIRST and default. Routing every row to one account is
              the override, not the norm — a multi-account export would otherwise
              all land in one place silently. */}
          <option value="">Auto-detect from the file</option>
          {(accounts ?? []).map((a) => (
            <option key={String(a.id)} value={String(a.id)}>{a.name || '(unnamed)'}</option>
          ))}
        </select>

        <input
          ref={fileRef}
          type="file"
          aria-label="Choose an export file"
          accept=".csv,.tsv,.txt,.xlsx"
          disabled={!ready || phase === 'writing'}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onPick(f);
          }}
        />
      </div>

      {/* ── the drop zone ──────────────────────────────────────────────────
          ⚠ Same `onPick` as the picker, deliberately: two entry points that
          each parse would be two places to forget the xlsx branch. It is a
          second way to hand over a file, not a second import path.

          ⚠ `onDragOver` MUST preventDefault or the browser navigates away to
          render the dropped file, losing whatever was on screen — the drop
          silently "does nothing" and takes the page with it. */}
      <div
        className={`import-drop ${dragging ? 'over' : ''}`}
        data-testid="import-drop"
        // ⚠ `role` + `tabIndex` + Enter/Space, not a bare `onClick`. A div that
        // only responds to a mouse is a control half this tab's users cannot
        // reach; the trade table already solves it exactly this way. The
        // labelled `<input type="file">` above stays the primary chooser — this
        // is a second way to reach the SAME one, which is why its accessible
        // name is the caption rather than a duplicate of the input's label.
        role="button"
        tabIndex={0}
        onClick={browse}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          // Space scrolls the page on a focused non-button otherwise.
          e.preventDefault();
          browse();
        }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!ready || phase === 'writing') return;
          const f = e.dataTransfer?.files?.[0];
          // ⚠ Only the FIRST file. Beta imports one at a time and the preview is
          // built around a single file's counts; quietly taking one of five
          // dropped files would be worse than saying so, which is what the
          // caption does.
          if (f) void onPick(f);
        }}
      >
        Drop a CSV, TSV or XLSX export here, or click to browse
      </div>

      {!ready ? <p className="muted">Loading accounts and contracts…</p> : null}
      {phase === 'reading' ? <p className="muted" role="status">Reading the file…</p> : null}
      {phase === 'writing' ? <p className="muted" role="status">Writing trades…</p> : null}

      {phase === 'failed' && message ? (
        <div role="alert" className="import-alert">
          <b>Nothing was imported.</b> {message}
          <div><button type="button" className="btn" onClick={reset}>Try another file</button></div>
        </div>
      ) : null}

      {phase === 'done' && message ? (
        <p className="green" role="status">{message}</p>
      ) : null}

      {/* ── the preview: nothing is written until this is confirmed ────────── */}
      {phase === 'previewing' && preview ? (
        <div className="import-preview" data-testid="import-preview">
          <p>
            <b>{preview.fileName}</b> · read as <b>{preview.format}</b>
          </p>
          <ul className="jp-list">
            <li className={preview.toInsert.length ? 'green' : 'muted'}>
              <b>{preview.toInsert.length}</b> new trade{preview.toInsert.length === 1 ? '' : 's'} to import
            </li>
            {preview.duplicates ? (
              <li className="muted">
                <b>{preview.duplicates}</b> already in your book — skipped by the duplicate check
              </li>
            ) : null}
            {preview.unassigned ? (
              <li className="muted">
                <b>{preview.unassigned}</b> could not be matched to an account and will import
                as <b>Unassigned</b>
              </li>
            ) : null}
            {preview.parserSkipped ? (
              <li className="muted">
                <b>{preview.parserSkipped}</b> row{preview.parserSkipped === 1 ? '' : 's'} the
                parser could not use
              </li>
            ) : null}
            {/* 🚨 THE NUMBER THAT EXPLAINS A SHORT FILE. These contracts closed a
                position opened before the file starts — an overnight hold, or a
                part-day export. They were real; there is simply no entry price
                here to value them with. Pairing them anyway is what inverted a
                whole session of Josh's on 2026-08-05. */}
            {preview.carriedIn ? (
              <li className="muted">
                <b>{preview.carriedIn}</b> contract{preview.carriedIn === 1 ? '' : 's'} closed a
                position opened <b>before this file starts</b> — not imported, because the file
                has no entry price for them. Export from the day you opened it to capture them.
              </li>
            ) : null}
            {/* 🚨 THE FILE DISAGREEING WITH US IS THE LOUDEST THING ON THIS
                SCREEN. It means the fills were paired wrongly, which changes
                sides, prices and P&L while still producing plausible trades —
                the exact failure that inverted a whole session on 2026-08-05
                and was not noticed for four days. */}
            {/* 🚨 A SHEET SWAP MUST NEVER BE SILENT. The numbers the user sees
                here will differ from the ones in their platform if we quietly
                used a different sheet, and their first instinct will be to
                distrust the app rather than the file. */}
            {preview.degraded ? (
              <li className="red">
                <b>
                  ⚠ Your platform's per-fill sheet is incomplete —{' '}
                  {preview.degraded.droppedNoPrice} fill
                  {preview.degraded.droppedNoPrice === 1 ? '' : 's'} with a quantity but no
                  price{preview.degraded.symbols.length
                    ? ` (${preview.degraded.symbols.join(', ')})` : ''}.
                </b>{' '}
                {preview.degraded.reason === 'atas-fills-incomplete'
                  ? <>Using the summary sheet instead, which matches your platform's own
                      totals. Entry times are rounded to the minute and scale-outs are
                      collapsed, so the excursion detail is coarser.</>
                  : <>There is no summary sheet to fall back to, so some trades are
                      missing and others may be paired wrongly. Re-export with prices
                      included before trusting these numbers.</>}
              </li>
            ) : null}
            {preview.positionMismatches ? (
              <li className="red">
                <b>
                  ⚠ On {preview.positionMismatches} fill
                  {preview.positionMismatches === 1 ? '' : 's'}, the position in the file does
                  not match the position we worked out.
                </b>{' '}
                The trades below may have the wrong sides or prices. Worth checking a few
                against your platform before importing.
              </li>
            ) : null}
            {/* 🚨 SAYING SO WHEN THE CHECKS COULD NOT RUN. Silence otherwise reads
                as "nothing wrong found" when the truth is "most of this file could
                not be checked" — these rows carry no position size and label every
                fill Open, so both self-checks are blind on them. #35. */}
            {/* 🚨 SAID OUT LOUD, because the entry price came from a DIFFERENT
                file. A trade appearing with an entry the user cannot find in
                the file they just imported would be alarming, and it is exactly
                the sort of thing that should be visible and undoable rather
                than discovered later. #12. */}
            {preview.carriedInClosed.length ? (
              <li>
                <b>
                  {preview.carriedInClosed.length} position
                  {preview.carriedInClosed.length === 1 ? '' : 's'} you were already holding
                  {preview.carriedInClosed.length === 1 ? ' was' : ' were'} closed by this file.
                </b>{' '}
                {preview.carriedInClosed.map((c) => (
                  `${c.qty} ${c.contract} opened ${String(c.openedAt).slice(0, 10)}`
                )).join(', ')}
                {' — '}the entry price comes from that earlier import, so
                {preview.carriedInClosed.length === 1 ? ' this trade spans' : ' these trades span'}
                {' '}more than one session.
              </li>
            ) : null}
            {preview.openCloseConflicts ? (
              <li className="red">
                <b>
                  ⚠ {preview.openCloseConflicts} fill
                  {preview.openCloseConflicts === 1 ? '' : 's'} that your platform calls an
                  OPENING trade had to be used to CLOSE one.
                </b>{' '}
                That usually means a position was already open before this file starts, and
                pairing from here on may be shifted by one leg. Check the first trade of each
                symbol against your platform before importing.
              </li>
            ) : null}
            {preview.unverifiedFills ? (
              <li>
                <b>
                  {preview.unverifiedFills} of {preview.totalFills} fills were filled in by
                  your platform after the fact
                </b>{' '}
                — those rows carry no running position size and label every fill as an
                opening one, so the checks above cannot see them. The trades are still
                imported; they just have not been verified.
              </li>
            ) : null}
            {/* ⚠ REJECTIONS ARE SHOWN, with the symbol. An uncatalogued contract is
                the one failure a user can actually act on. */}
            {preview.rejected.length ? (
              <li className="red">
                <b>{preview.rejected.length}</b> rejected — uncatalogued contract
                {preview.rejected.length === 1 ? '' : 's'}:{' '}
                {[...new Set(preview.rejected.map((r) => r.contract))].join(', ')}
              </li>
            ) : null}
          </ul>

          <div className="import-row">
            <button
              type="button"
              className="btn primary"
              disabled={preview.toInsert.length === 0}
              onClick={() => void commit()}
            >
              {preview.toInsert.length
                ? `Import ${preview.toInsert.length} trade${preview.toInsert.length === 1 ? '' : 's'}`
                : 'Nothing to import'}
            </button>
            <button type="button" className="btn" onClick={reset}>Cancel</button>
          </div>
        </div>
      ) : null}

      <p className="muted">
        Reads Sierra Chart fills, ATAS exports (CSV or XLSX), Tradovate
        (Performance Report and Order History), Rithmic, TopstepX, Tradesea,
        MotiveWave, Trading Technologies and Jigsaw “MyTrades” workbooks — every
        format the current app reads.
      </p>
    </div>
  );
}
